import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import StageShell from "@/components/StageShell";
import ClientForm from "@/components/ClientForm";
import Uploads from "@/components/intake/Uploads";
import ParsedResumeEditor from "@/components/intake/ParsedResumeEditor";
import MetricsCall from "@/components/intake/MetricsCall";
import FactSheet from "@/components/intake/FactSheet";
import { useRun } from "@/hooks/useRun";
import { api, errMsg } from "@/lib/api";

function SubSteps({ steps, tab, setTab }) {
  return (
    <div className="flex gap-2" data-testid="intake-substeps">
      {steps.map((s, i) => (
        <button key={s.id} data-testid={`tab-${s.id}`} onClick={() => setTab(s.id)}
          className={`flex-1 text-left rounded-lg border px-4 py-3 transition-colors ${tab === s.id ? "border-blue-500/60 bg-blue-600/10" : "border-border hover:border-slate-600"}`}>
          <div className="flex items-center gap-2">
            <span className={`h-5 w-5 rounded-full grid place-items-center text-[10px] font-mono ${s.done ? "bg-emerald-500 text-white" : "border border-slate-600 text-slate-400"}`}>{s.done ? <Check size={11} /> : i + 1}</span>
            <span className="text-sm font-medium">{s.label}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 ml-7">{s.hint}</div>
        </button>
      ))}
    </div>
  );
}

export default function Stage0({ job, refresh }) {
  const [client, setClient] = useState(job.client);
  const [files, setFiles] = useState([]);
  const [facts, setFacts] = useState([]);
  const [tab, setTab] = useState(null);
  const cid = job.client_id;

  const load = useCallback(async () => {
    const [c, f, fa] = await Promise.all([api.get(`/clients/${cid}`), api.get(`/clients/${cid}/files`), api.get(`/clients/${cid}/facts`)]);
    setClient(c.data); setFiles(f.data); setFacts(fa.data);
    return { c: c.data, f: f.data, fa: fa.data };
  }, [cid]);

  const runHook = useRun(job.id, 0, async () => { await load(); refresh(); setTab("parsed"); });
  const hasResume = files.some((f) => f.kind === "resume");
  const roles = client.parsed_resume?.roles || [];
  const verified = (rid) => facts.filter((f) => f.role_id === rid && f.type === "number" && f.verified).length;
  const steps = [
    { id: "intake", label: "Details & resume", hint: hasResume ? "Saved and uploaded" : "Fill the form, upload the old resume", done: !!(client.visa_status && client.current_location && hasResume) },
    { id: "parsed", label: "Check parsed resume", hint: roles.length ? `${roles.length} roles found` : "AI reads it after upload", done: roles.length > 0 },
    { id: "call", label: "Client questions (optional)", hint: roles.length ? `${roles.filter((r) => verified(r.id) > 0).length}/${roles.length} roles have answers` : "After parsing", done: roles.length > 0 },
    { id: "facts", label: "Review facts (optional)", hint: `${facts.filter((f) => f.verified).length}/${facts.length} verified`, done: facts.length > 0 && facts.every((f) => f.verified) },
  ];

  useEffect(() => {
    load().then(({ c, f }) => {
      const hasR = f.some((x) => x.kind === "resume");
      setTab(!hasR || !c.visa_status ? "intake" : c.parsed_resume ? "call" : "parsed");
    });
  }, [load]);

  const saveClient = async (c) => {
    try { await api.put(`/clients/${cid}`, c); toast.success("Intake saved"); load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const factsChanged = () => { load(); refresh(); };
  const uploaded = () => { load(); refresh(); setTimeout(() => runHook.track(), 600); };
  if (!tab) return null;

  return (
    <StageShell job={job} stage={0} refresh={refresh} runHook={runHook}
      runs={[{ action: "intake_parse", label: client.parsed_resume ? "Re-read resume" : "Read resume with AI", disabled: !hasResume, testid: "intake-parse-button" }]}>
      <SubSteps steps={steps} tab={tab} setTab={setTab} />
      {tab === "intake" && (
        <div className="space-y-5 fade-in">
          <Uploads cid={cid} jobId={job.id} files={files} onChange={uploaded} />
          <div className="card-surface"><ClientForm key={client.updated_at} initial={client} onSubmit={saveClient} /></div>
        </div>
      )}
      {tab === "parsed" && <ParsedResumeEditor client={client} files={files} onSaved={load} />}
      {tab === "call" && <MetricsCall client={client} facts={facts} onChange={factsChanged} />}
      {tab === "facts" && <FactSheet client={client} facts={facts} onChange={factsChanged} />}
    </StageShell>
  );
}
